import { addDoc, collection, Timestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import { useAuth } from "../../components/authContext";
import CustomLink from "../../components/hoverlink";
import LiftTrackerForm from "./components/lift-tracker";
import RunTrackerForm from "./components/run-tracker";
import WorkoutHistory from "./components/workouthistory";

const TrackerPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"lift" | "run">("lift");
  const [refreshKey, setRefreshKey] = useState(0);
  const [supersets, setSupersets] = useState([
    { exercise: "", sets: [{ reps: "", weight: "", rpe: "" }] },
  ]);

  const [runDetails, setRunDetails] = useState({
    distance: "",
    paceMinutes: "",
    paceSeconds: "",
    timeHours: "",
    timeMinutes: "",
    timeSeconds: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    const stravaData = localStorage.getItem("stravaRunData");
    if (stravaData) {
      const parsed = JSON.parse(stravaData);
      setRunDetails({
        distance: parsed.distance,
        paceMinutes: Math.floor(parsed.pace).toString(),
        paceSeconds: Math.round((parsed.pace % 1) * 60).toString(),
        timeHours: Math.floor(parsed.time / 60).toString(),
        timeMinutes: Math.floor(parsed.time % 60).toString(),
        timeSeconds: Math.round((parsed.time % 1) * 60).toString(),
      });
      localStorage.removeItem("stravaRunData");
    }
  }, []);

  const handleSaveWorkout = async () => {
    if (!user) return;

    const workoutData: any = {
      uid: user.uid,
      createdAt: Timestamp.now(),
      exercises: [],
    };

    if (mode === "lift") {
      supersets.forEach((superset) => {
        if (!superset.exercise) return;
        workoutData.exercises.push({
          name: superset.exercise,
          mode: "lift",
          sets: superset.sets.map((s) => ({
            reps: parseInt(s.reps),
            weight: parseFloat(s.weight),
            rpe: parseFloat(s.rpe),
          })),
        });
      });
    } else if (mode === "run") {
      workoutData.exercises.push({
        name: "Run",
        mode: "run",
        sets: [
          {
            distance: parseFloat(runDetails.distance),
            pace:
              parseInt(runDetails.paceMinutes || "0") +
              parseInt(runDetails.paceSeconds || "0") / 60,
            time:
              parseInt(runDetails.timeHours || "0") * 60 +
              parseInt(runDetails.timeMinutes || "0") +
              parseInt(runDetails.timeSeconds || "0") / 60,
          },
        ],
      });
    }

    await addDoc(collection(db, `users/${user.uid}/workouts`), workoutData);

    setSupersets([{ exercise: "", sets: [{ reps: "", weight: "", rpe: "" }] }]);
    setRunDetails({
      distance: "",
      paceMinutes: "",
      paceSeconds: "",
      timeHours: "",
      timeMinutes: "",
      timeSeconds: "",
    });
    setRefreshKey((prev) => prev + 1);
  };

  if (!user) return null; // avoid flash of content before redirect

  return (
    <div style={{ padding: "2rem" }}>
      <h1>Workout Tracker</h1>
      <p>Welcome, {user.displayName}</p>
      <div>
        <label>
          <input
            type="radio"
            value="lift"
            checked={mode === "lift"}
            onChange={() => setMode("lift")}
          />
          Lift
        </label>
        <label style={{ marginLeft: "12px" }}>
          <input
            type="radio"
            value="run"
            checked={mode === "run"}
            onChange={() => setMode("run")}
          />
          Run
        </label>
      </div>

      {mode === "lift" ? (
        <LiftTrackerForm
          supersets={supersets}
          setSupersets={setSupersets}
          onSave={handleSaveWorkout}
        />
      ) : (
        <RunTrackerForm
          runDetails={runDetails}
          setRunDetails={setRunDetails}
          onSave={handleSaveWorkout}
        />
      )}

      <hr />
      <WorkoutHistory uid={user.uid} key={refreshKey} />
      <p
        style={{
          fontSize: "12px",
          textAlign: "center",
          marginBottom: "8px",
        }}
      >
        <CustomLink
          href="/edit-workouts"
          style={{ textDecoration: "underline" }}
        >
          I made a mistake and need to edit it 🥹
        </CustomLink>
      </p>
      <div style={{ marginTop: "16px" }}>
        <CustomLink onClick={logout} href={""}>
          Logout
        </CustomLink>
      </div>
    </div>
  );
};

export default TrackerPage;
